import { uniq } from 'es-toolkit';

import { ProductionComponentInstance } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { getBundlesByScheduleLineGroupGuid } from '../selectors/bundles';
import { getBundlesByScheduleGroupGuid } from '../selectors/getBundlesByScheduleGroupGuid';
import { getProductionComponentInstancesByBundleGuid } from '../selectors/getProductionComponentInstancesByBundleGuid';
import { getProductionComponentInstancesByProductionGroupGuid } from '../selectors/getProductionComponentInstancesByProductionGroupGuid';
import { getProductionComponentInstancesByTruckloadGuid } from '../selectors/getProductionComponentInstancesByTruckloadGuid';
import {
  componentDesignsWithInformationLoaded,
  setLoadingStatus,
} from '../store/componentDesignsWithInformationByAnalysisSetGuidSlice';
import { AsyncThunkActionVoid } from './thunkActions';

export function loadComponentsForProductionGroup(productionGroupGuid: string): AsyncThunkActionVoid {
  return async function loadComponentsForProductionGroupThunkAction(dispatch, getState) {
    const state = getState();

    const instancesForProductionGroup =
      getProductionComponentInstancesByProductionGroupGuid(state)[productionGroupGuid];

    await dispatch(loadComponentsForProductionComponentInstances(instancesForProductionGroup));
  };
}

export function loadComponentsForScheduleGroup(scheduleGroupGuid: string): AsyncThunkActionVoid {
  return async function loadComponentsForScheduleGroupThunkAction(dispatch, getState) {
    const state = getState();

    const bundlesForScheduleGroup = getBundlesByScheduleGroupGuid(state)[scheduleGroupGuid];
    const instancesByBundleGuid = getProductionComponentInstancesByBundleGuid(state);
    const productionComponentInstancesForScheduleGroup = bundlesForScheduleGroup.flatMap(
      (bundle) => instancesByBundleGuid[bundle.guid],
    );

    await dispatch(loadComponentsForProductionComponentInstances(productionComponentInstancesForScheduleGroup));
  };
}

export function loadComponentsForScheduleLineGroup(scheduleLineGroupGuid: string): AsyncThunkActionVoid {
  return async function loadComponentsForScheduleLineGroupThunkAction(dispatch, getState) {
    const state = getState();

    const bundlesForScheduleLineGroup = getBundlesByScheduleLineGroupGuid(state)[scheduleLineGroupGuid];
    const instancesByBundleGuid = getProductionComponentInstancesByBundleGuid(state);
    const productionComponentInstancesForScheduleLineGroup = bundlesForScheduleLineGroup.flatMap(
      (bundle) => instancesByBundleGuid[bundle.guid],
    );

    await dispatch(loadComponentsForProductionComponentInstances(productionComponentInstancesForScheduleLineGroup));
  };
}

export function loadComponentsForTruckload(truckloadGuid: string): AsyncThunkActionVoid {
  return async function loadComponentsForProductionGroupThunkAction(dispatch, getState) {
    const state = getState();

    const instancesForTruckload = getProductionComponentInstancesByTruckloadGuid(state)[truckloadGuid];

    await dispatch(loadComponentsForProductionComponentInstances(instancesForTruckload));
  };
}

export function loadComponentsForProductionComponentInstances(
  productionComponentInstances: readonly ProductionComponentInstance[],
  spinner = true,
): AsyncThunkActionVoid {
  return async function loadComponentsForProductionComponentInstancesThunkAction(dispatch, getState) {
    const spinnerId = spinner ? dispatch(addSpinnerMessage('Loading components...')) : null;

    let analysisSetGuidsNotLoaded: string[] = [];
    try {
      analysisSetGuidsNotLoaded = uniq(
        productionComponentInstances
          .map((productionComponentInstance) => productionComponentInstance.analysisSetGuid)
          .filter(
            (analysisSetGuid) => !(analysisSetGuid in getState().componentDesignsWithInformationByAnalysisSetGuid),
          ),
      );

      if (analysisSetGuidsNotLoaded.length === 0) return;

      dispatch(setLoadingStatus({ analysisSetGuids: analysisSetGuidsNotLoaded, status: 'loading' }));

      const componentDesignsWithInformationByAnalysisSetGuid =
        await designService.analysisSets.fetchComponentsFromAnalysisSets(analysisSetGuidsNotLoaded);
      dispatch(componentDesignsWithInformationLoaded(componentDesignsWithInformationByAnalysisSetGuid));
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Error loading components'));
      dispatch(setLoadingStatus({ analysisSetGuids: analysisSetGuidsNotLoaded, status: 'error-loading' }));
    } finally {
      if (spinnerId != null) dispatch(removeSpinnerMessage(spinnerId));
    }
  };
}
